/**
 * Settings Page - Fully Functional
 *
 * All sections connect to real backend APIs:
 * - Profile Settings: Updates via /api/users/:id
 * - Connected Accounts: Fetches from /api/connectors/connections
 * - Proactive Alerts: Links to /settings/alerts and /settings/alert-rules
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  NotificationsActive,
  Rule,
  ChevronRight,
  Link as LinkIcon,
  CheckCircle,
  Error as ErrorIcon,
  Sync
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import { connectorService } from '../../services/connectorService';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: ''
  });

  // Connectors state
  const [connectors, setConnectors] = useState([]);
  const [loadingConnectors, setLoadingConnectors] = useState(true);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load data on mount
  useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName || '',
        email: user.email || ''
      });
    }
    loadConnectors();
  }, [user]);

  // Load real connectors from API
  const loadConnectors = async () => {
    setLoadingConnectors(true);
    try {
      const result = await connectorService.getConnections();
      setConnectors(result.connectors || []);
    } catch (error) {
      console.error('Error loading connectors:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load connected accounts',
        severity: 'error'
      });
    } finally {
      setLoadingConnectors(false);
    }
  };

  // Save profile to backend
  const handleProfileSave = async () => {
    if (!user?.id) return;

    setSavingProfile(true);
    try {
      await authService.updateProfile(user.id, {
        fullName: profileData.fullName,
        email: profileData.email
      });

      // Refresh user context if available
      if (refreshUser) {
        await refreshUser();
      }

      setEditingProfile(false);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update profile',
        severity: 'error'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  // Cancel profile edit
  const handleProfileCancel = () => {
    setProfileData({
      fullName: user?.fullName || '',
      email: user?.email || ''
    });
    setEditingProfile(false);
  };

  // Format connector status
  const getStatusChip = (connector) => {
    // /connections may not return `status`; fall back to the last job and lifecycle_state
    const lifecycleStatus = ['active', 'first_sync_done', 'scheduled'].includes(connector.lifecycleState)
      ? 'active'
      : connector.lifecycleState;
    const jobStatus = connector.lastJobStatus === 'failed' ? 'failed'
      : connector.lastJobStatus === 'running' ? 'syncing'
      : null;
    const status = connector.effectiveStatus || connector.status || jobStatus || lifecycleStatus || 'unknown';

    if (status === 'active' || status === 'connected') {
      return <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />;
    } else if (status === 'syncing') {
      return <Chip label="Syncing" color="info" size="small" icon={<Sync />} />;
    } else if (status === 'failed') {
      return <Chip label="Failed" color="error" size="small" icon={<ErrorIcon />} />;
    } else {
      return <Chip label={status} size="small" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom color="primary">
        Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Edit sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Profile Settings</Typography>
              </Box>

              {editingProfile ? (
                <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={profileData.fullName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, fullName: e.target.value }))}
                    disabled={savingProfile}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={savingProfile}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={savingProfile ? <CircularProgress size={20} color="inherit" /> : <Save />}
                      onClick={handleProfileSave}
                      disabled={savingProfile}
                    >
                      {savingProfile ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleProfileCancel}
                      disabled={savingProfile}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Name:</strong> {user?.fullName || 'Not set'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {user?.email || 'Not set'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Role:</strong> {user?.role || 'user'}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditingProfile(true)}
                    sx={{ mt: 2 }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Proactive Alerts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <NotificationsActive sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Proactive Alerts</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure AI-powered alerts for performance changes, anomalies, and opportunities.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <NotificationsActive sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="subtitle2">Alert Preferences</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Topics, severity, timing, delivery
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        endIcon={<ChevronRight />}
                        onClick={() => navigate('/settings/alerts')}
                      >
                        Configure
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rule sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="subtitle2">Custom Alert Rules</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Threshold-based alerts
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        endIcon={<ChevronRight />}
                        onClick={() => navigate('/settings/alert-rules')}
                      >
                        Manage
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Connected Accounts - Real Data */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Connected Accounts</Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/connectors')}
                >
                  Manage Connectors
                </Button>
              </Box>

              {loadingConnectors ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="40%" height={24} />
                        <Skeleton width="25%" height={20} />
                      </Box>
                      <Skeleton width={80} height={32} />
                    </Box>
                  ))}
                </Box>
              ) : connectors.length === 0 ? (
                <Alert severity="info">
                  No connectors configured yet.{' '}
                  <Button size="small" onClick={() => navigate('/connectors')}>
                    Add your first connector
                  </Button>
                </Alert>
              ) : (
                <List disablePadding>
                  {connectors.map((connector, index) => (
                    <ListItem
                      key={connector.uniqueKey || connector.id}
                      divider={index < connectors.length - 1}
                      sx={{ px: 0 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={500}>
                            {connector.name || connector.platform_display_name || 'Unnamed Connector'}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {connector.platform_display_name || connector.platform_name}
                            </Typography>
                            {connector.brand_name && (
                              <Typography variant="body2" color="text.secondary" component="span">
                                {connector.brand_name}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" component="span">
                              Connected {formatDate(connector.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusChip(connector)}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
